import React, { useEffect, useState } from "react";
import i18n from "../../i18n";
import { HashRouter as Router, Route } from "react-router-dom";
import styled from "styled-components";
import { Stores } from "../../types";
import { SessionStore } from "../../stores/Session";
import { StoresContext } from "../../contexts";
import Login from "../Login";
import ForgotPassword from "../ForgotPassword";
import ChangePassword from "../ChangePassword";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { RedirectToUserBoard } from "../RedirectToUserBoard";
import ProgramCategories from "../ProgramCategories";
import { ArticleStore } from "../../stores/ArticleStore";
import { CategoryStore } from "../../stores/CategoryStore";
import ProgramsList from "../Programs";
import { ProgramStore } from "../../stores/ProgramStore";
import { UserStore } from "../../stores/UserStore";
import { PrivateRoute } from "../../components/common/PrivateRoute";
import ProgramDetail from "../ProgramDetail";
import Banners from "../Banners";
import { BannerStore } from "../../stores/BannerStore";
import AdvertisingCircuit from "../AdvertisingCircuit";
import { AdCircuitStore } from "../../stores/AdCircuitStore";
import AdvertisingDetail from "../AdvertisingDetail";
import { TusClientProvider } from "use-tus";
import MediaList from "../MediaList";
import IconsList from "../IconsList";
import MenuLinks from "../MenuLinks";
import Footer from "../Footer";
import Streaming from "../Streaming";
import { ConfigStore } from "../../stores/ConfigStore";
import Firestick from "../Firestick";
import MultimediaCategories from "../MultimediaCategories";
import PushNotifications from "../PushNotifications";
import { PushNotificationsStore } from "../../stores/PushNotificationsStore";
import { StyledBadge } from "../../components/common/Header/HeaderDropdownElements";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

const Root: React.FC = observer(() => {
  const [stores] = useState<Stores>(() => {
    const root: Stores = {} as Stores;
    root.session = new SessionStore(root);
    root.categoryStore = new CategoryStore(root);
    root.articleStore = new ArticleStore(root);
    root.programStore = new ProgramStore(root);
    root.userStore = new UserStore(root);
    root.bannerStore = new BannerStore(root);
    root.adCircuitStore = new AdCircuitStore(root);
    root.configStore = new ConfigStore(root);
    root.pushNotificationsStore = new PushNotificationsStore(root);
    return root;
  });
  const { t } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage("it");
    dayjs.locale("it");
  }, []);

  useEffect(() => {
    if (!stores.session.isInitialized) {
      stores.session.initialize();
    }
    if (!stores.userStore.vimeoUser?.uri) {
      stores.userStore.fetchVimeoUser();
    }
  }, []);

  useEffect(() => {
    if (stores.userStore.vimeoUserError) {
      toast(t("messages.ui.vimeoUserError"), {
        position: "bottom-right",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        type: "error",
        theme: "colored",
        onClose: () => (stores.userStore.vimeoUserError = false),
      });
    }
  }, [stores.userStore, stores.userStore.vimeoUserError, t]);

  return (
    <TusClientProvider>
      <StoresContext.Provider value={stores}>
        <Router>
          <Route exact path={`/`} component={RedirectToUserBoard} />
          <Route path={"/login"} component={Login} />
          <Route path={"/forgot-password"} component={ForgotPassword} />
          <Route path={"/change-password"} component={ChangePassword} />
          <PrivateRoute
            path={"/program-categories"}
            component={ProgramCategories}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/multimedia-categories"}
            component={MultimediaCategories}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/programs"}
            component={ProgramsList}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/program/:id"}
            component={ProgramDetail}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/banners"}
            component={Banners}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/advertising-circuit"}
            component={AdvertisingCircuit}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/advertise/:id"}
            component={AdvertisingDetail}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/media"}
            component={MediaList}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/icons"}
            component={IconsList}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/menu-link"}
            component={MenuLinks}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/footer"}
            component={Footer}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/streaming"}
            component={Streaming}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/firestick-app"}
            component={Firestick}
            isAuthenticated={stores.session.isLogged}
          />
          <PrivateRoute
            path={"/push-notifications"}
            component={PushNotifications}
            isAuthenticated={stores.session.isLogged}
          />
        </Router>
        <ToastContainer />
      </StoresContext.Provider>
    </TusClientProvider>
  );
});

export default styled(Root)`
  & {
    min-height: 100vh;
  }
`;
